import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { SpecialistAssignmentService } from '@/lib/services/specialist-assignment-service'
import type { AssignedCaregiver, SpecialistWithAssignments } from '@/types/specialist-assignment'

interface TransferCaregiverDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  caregiver: AssignedCaregiver
  currentSpecialist: SpecialistWithAssignments
  allSpecialists: SpecialistWithAssignments[]
  onSuccess: () => void
}

export function TransferCaregiverDialog({
  open,
  onOpenChange,
  caregiver,
  currentSpecialist,
  allSpecialists,
  onSuccess
}: TransferCaregiverDialogProps) {
  const [newSpecialistId, setNewSpecialistId] = useState('')
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  const selectedSpecialist = allSpecialists.find(s => s.specialist_id === newSpecialistId)
  const hasChildren = caregiver.children_count > 0

  const handleTransfer = async () => {
    if (!newSpecialistId) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Please select a specialist'
      })
      return
    }

    setLoading(true)
    try {
      const result = await SpecialistAssignmentService.transferCaregiver({
        caregiver_id: caregiver.id,
        old_specialist_id: currentSpecialist.specialist_id,
        new_specialist_id: newSpecialistId,
        reason: reason.trim() || undefined
      })

      if (result.success) {
        toast({
          title: 'Transfer successful',
          description: `${caregiver.name} has been transferred to ${selectedSpecialist?.specialist_name}`
        })
        onSuccess()
        onOpenChange(false)
        setNewSpecialistId('')
        setReason('')
      } else {
        toast({
          variant: 'destructive',
          title: 'Transfer failed',
          description: result.error || 'An error occurred'
        })
      }
    } catch (error) {
      console.error('Transfer error:', error)
      toast({
        variant: 'destructive',
        title: 'Transfer failed',
        description: 'An unexpected error occurred'
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Transfer Caregiver</DialogTitle>
          <DialogDescription>
            Transfer {caregiver.name} from {currentSpecialist.specialist_name} to another specialist
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {hasChildren && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This caregiver has {caregiver.children_count} {caregiver.children_count === 1 ? 'child' : 'children'}. 
                All child data and sleep plans will remain accessible to the new specialist.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="new-specialist">New Specialist</Label>
            <Select value={newSpecialistId} onValueChange={setNewSpecialistId}>
              <SelectTrigger id="new-specialist">
                <SelectValue placeholder="Select a specialist" />
              </SelectTrigger>
              <SelectContent>
                {allSpecialists.map(specialist => (
                  <SelectItem key={specialist.specialist_id} value={specialist.specialist_id}>
                    {specialist.specialist_name} ({specialist.caregiver_count} caregivers)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Textarea
              id="reason"
              placeholder="Why is this transfer being made?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleTransfer} disabled={loading || !newSpecialistId}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
